<template>
  <ProductCard
    :product="viewModel"
    :currency="currency"
    :quantity="quantity"
    :cart-quantity="cartQuantity"
    :selections="selections"
    :sold-out="soldOut"
    :unavailable="unavailable"
    :highlighted="isHighlighted"
    :line-price-cents="linePriceCents"
    :line-original-price-cents="lineOriginalPriceCents"
    :base-price-original-cents="basePriceOriginalCents"
    :product-id="product.id"
    :cart-stepper="!inCartView"
    :decrease-disabled="decreaseDisabled"
    :increase-disabled="increaseDisabled"
    test-id-prefix="v2-product-card"
    @open="open"
    @increase="increase"
    @decrease="decrease"
    @remove="remove"
  />
</template>

<script>
import { inject, ref } from 'vue'
import ProductCard from '@/components/Product/ProductCard.vue'
import {
  cartItemVariantOptionsToSelection,
  fromV2,
  lineUnitPriceCents,
  selectionSummary
} from '@/models/productViewModel'
import { v2BasePriceCents, v2DefaultPriceCents } from '@/utils/v2ProductBasePrice'
import { isProductSoldOut } from '@/utils/upsellAvailability'
import { lineQuantityRules, steppedDownQuantity, steppedUpQuantity } from '@/utils/cartLineQuantity'
import { trackCartLineQuantityChange } from '@/utils/cartLineAnalytics'
import { blankSubmittableCartItem } from '@/utils/submittableCartItem'

export default {
  name: 'V2ProductCard',

  components: { ProductCard },

  props: {
    product: {
      type: Object,
      required: true
    },
    submittableCartItem: {
      type: Object,
      default: null
    },
    // Everything this product holds across every line, for the card's in-cart styling. A menu card
    // has no line of its own, so its own `quantity` is 0.
    cartQuantity: {
      type: Number,
      default: null
    },
    // The product's lines in the cart, in the order they were added - what the stepper's minus
    // takes one away from (BOT-1730).
    cartLines: {
      type: Array,
      default: () => []
    },
    // The server-priced counterpart of `submittableCartItem`, matched by V2Category. It is what
    // carries the line's discounted unit price; the local item only knows quantity and options.
    v2CartItem: {
      type: Object,
      default: null
    },
    pricingTiers: {
      type: Array,
      default: () => []
    },
    isUpsell: {
      type: Boolean,
      default: false
    },
    // True in the cart-only view, where V2Category renders one card per cart line rather than one
    // per product. Deliberately not `inCart`, which is already a computed here meaning "this card
    // has a line" - a different question from "which view are we in".
    inCartView: {
      type: Boolean,
      default: false
    },
    // Set by V2NoLongerAvailableCategory. These products are synthesized from unavailable cart
    // lines and are absent from the store snapshot the modal host reads, so the card offers
    // removal instead of a modal it could only render empty.
    unavailable: {
      type: Boolean,
      default: false
    }
  },

  emits: ['open'],

  setup() {
    const v2OpenProductModal = inject('v2OpenProductModal', null)
    const v2UpdateCartItem = inject('v2UpdateCartItem', null)
    const v2HighlightedProductId = inject('v2HighlightedProductId', ref(null))
    const v2ActivePricingTierId = inject('v2ActivePricingTierId', null)
    const v2CustomerMembershipTierId = inject('v2CustomerMembershipTierId', null)
    // The tier a customer with no membership would be priced at - the "was" side of a
    // membership discount.
    const v2DefaultMembershipTierId = inject('v2DefaultMembershipTierId', null)
    const v2OpenForOrders = inject('v2OpenForOrders', null)
    const v2ShowCart = inject('v2ShowCart', null)
    return {
      v2OpenProductModal,
      v2UpdateCartItem,
      v2HighlightedProductId,
      v2ActivePricingTierId,
      v2CustomerMembershipTierId,
      v2DefaultMembershipTierId,
      v2OpenForOrders,
      v2ShowCart
    }
  },

  computed: {
    // The card renders off the same normalized view model the modal receives, so a chip or a
    // price can never say one thing on the menu and another once the modal is open.
    viewModel() {
      return fromV2({ product: this.product, basePriceCents: this.basePriceCents })
    },
    basePriceCents() {
      return v2BasePriceCents(this.product, {
        pricingTierId: this.v2ActivePricingTierId,
        membershipTierId: this.v2CustomerMembershipTierId,
        isUpsell: this.isUpsell
      })
    },
    currency() {
      return this.product.price_formatted?.currency_iso || 'USD'
    },
    quantity() {
      return this.submittableCartItem?.quantity || 0
    },
    inCart() {
      return this.quantity > 0
    },
    // A line already in the cart is never sold out, whatever the snapshot says about remaining stock.
    soldOut() {
      if (this.inCart) return false
      return isProductSoldOut(this.product)
    },
    // The unit the server priced this line at, net of discounts. `display_price` is the richer
    // shape and carries the strike-through alongside it; a line priced before that shape existed
    // only has a line total, which divides back out because the backend built it the same way
    // (CategoryProductStoreCartItem#assign_net_prices_cents).
    serverUnitPriceCents() {
      const item = this.v2CartItem
      if (!item) return null
      if (item.display_price && item.display_price.price != null) return Number(item.display_price.price)
      const quantity = Number(item.quantity) || 0
      if (item.total_price != null && quantity > 0) return Math.round(Number(item.total_price) / quantity)
      return null
    },

    lineSelection() {
      return cartItemVariantOptionsToSelection(this.submittableCartItem?.cart_item_variant_options_attributes)
    },

    selections() {
      return selectionSummary(this.viewModel, this.lineSelection)
    },

    linePriceCents() {
      return lineUnitPriceCents({
        unitCents: this.serverUnitPriceCents,
        viewModel: this.viewModel,
        selection: this.lineSelection,
        quantity: this.quantity
      })
    },

    lineOriginalPriceCents() {
      return lineUnitPriceCents({
        unitCents: this.v2CartItem?.display_price?.strike_through_price ?? null,
        quantity: this.quantity
      })
    },

    basePriceOriginalCents() {
      return v2DefaultPriceCents(this.product, {
        pricingTierId: this.v2ActivePricingTierId,
        membershipTierId: this.v2CustomerMembershipTierId,
        defaultPricingTierId: this.defaultPricingTierId,
        defaultMembershipTierId: this.v2DefaultMembershipTierId,
        isUpsell: this.isUpsell
      })
    },

    defaultPricingTierId() {
      return this.pricingTiers.find((tier) => tier.is_default)?.id ?? null
    },

    isHighlighted() {
      return this.v2HighlightedProductId === this.product.id
    },
    editsExistingLine() {
      return this.inCartView && this.inCart
    },

    decreaseDisabled() {
      return this.v2OpenForOrders === false
    },

    // Only the plus that adds straight away can run out of room: one line, already at the most the
    // product allows.
    increaseDisabled() {
      if (this.viewModel.hasModifiers || this.cartLines.length !== 1) return false
      const line = this.cartLines[0]
      return steppedUpQuantity(Number(line.quantity) || 0, this.lineRules(line)) == null
    }
  },

  methods: {
    open() {
      // In the cart view a card *is* one cart line, so tapping it edits that line. In the full
      // product list the card stands for the product and a tap starts blank: ordering the same
      // product twice with different modifiers is the common case on a menu (two salads, one
      // large and one small), and pre-filling there would make the second variation harder to
      // build than the first. The quantity check keeps the stub cards - which carry a synthetic
      // `stub-<cpsp>` key and no line behind them - on the blank path.
      const payload = {
        cpspId: this.product.cpsp_id,
        frontendKey: this.editsExistingLine ? this.submittableCartItem.frontend_key || null : null,
        isUpsell: this.isUpsell
      }
      this.$emit('open', payload)
      if (this.v2OpenProductModal) this.v2OpenProductModal(payload)
    },

    // With nothing to choose, the plus adds one straight away: the first one, or one more on the only
    // line, by the rules the cart drawer's stepper uses. With variants it opens a fresh popup, so each
    // version is chosen on its own, and while the store is not taking orders the popup says so.
    increase() {
      if (this.viewModel.hasModifiers || this.v2OpenForOrders === false) {
        this.open()
        return
      }
      if (this.cartLines.length > 1) {
        this.showLinesInCart()
        return
      }
      const line =
        this.cartLines[0] ||
        blankSubmittableCartItem({ categoryProductStorePairId: this.product.cpsp_id, isUpsell: this.isUpsell })
      const to = steppedUpQuantity(Number(line.quantity) || 0, this.lineRules(line))
      if (to == null) {
        this.open()
        return
      }
      this.setLineQuantity(line, to)
    },

    // With one version in the cart the minus takes one away, by the rules the cart drawer's stepper
    // uses. With several it cannot know which one the customer means, so it shows them the cart, and
    // so does the plus.
    decrease() {
      if (this.cartLines.length > 1) {
        this.showLinesInCart()
        return
      }
      const line = this.cartLines[0]
      if (!line) return
      this.setLineQuantity(line, steppedDownQuantity(Number(line.quantity) || 0, this.lineRules(line)))
    },

    // Opens the cart pointing at this product's lines (BOT-1748).
    showLinesInCart() {
      if (!this.v2ShowCart) return
      this.v2ShowCart({ lineKeys: this.cartLines.map((line) => line.frontend_key), productName: this.product.name })
    },

    lineRules(line) {
      return lineQuantityRules(fromV2({ product: this.product, cartItem: line }))
    },

    setLineQuantity(line, to) {
      if (!this.v2UpdateCartItem) return
      trackCartLineQuantityChange(this.$store, {
        from: Number(line.quantity) || 0,
        to,
        isUpsell: this.isUpsell,
        productId: this.product.id,
        productName: this.product.name
      })
      this.v2UpdateCartItem({ ...line, quantity: to })
    },

    // Under V2NoLongerAvailableCategory this reaches updateUnavailableCartItem, which at a quantity
    // of 0 sends the explicit removal signal instead of re-attaching the cpsp to the cart.
    remove() {
      if (!this.v2UpdateCartItem || !this.submittableCartItem) return
      this.v2UpdateCartItem({ ...this.submittableCartItem, quantity: 0 })
    }
  }
}
</script>
